/**
 * ExamChecklist.js — Always-visible sidebar checklist
 * Shows live status of all exam readiness tasks.
 * Used in ManageExams as a persistent panel.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export default function ExamChecklist({ exam, onAction, readOnly = false }) {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!exam?.id) return;
    setLoading(true);
    Promise.all([
      axios.get(`/api/exams/${exam.id}/questions`).catch(() => ({ data: [] })),
      axios.get(`/api/exams/${exam.id}/enrollment-count`).catch(() => ({ data: { count: 0 } })),
      axios.get(`/api/exams/${exam.id}/instructors`).catch(() => ({ data: [] })),
    ]).then(([qRes, enRes, instrRes]) => {
      setStats({
        questions:   Array.isArray(qRes.data) ? qRes.data.length : 0,
        students:    enRes.data?.count ?? 0,
        instructors: Array.isArray(instrRes.data) ? instrRes.data.length : 0,
      });
    }).finally(() => setLoading(false));
  }, [exam?.id]);

  useEffect(() => { load(); }, [load]);

  if (!exam) return null;

  const checks = [
    // ── REQUIRED ───────────────────────────────────────────────────────────
    {
      id:'title', required:true,
      done: !!(exam.title?.trim() && exam.duration_minutes > 0 && exam.total_marks > 0),
      label: 'Exam Details', icon: '📝',
      desc: exam.title ? `${exam.duration_minutes}min · ${exam.total_marks} marks` : 'Title, duration & marks required',
      action: 'edit', actionLabel: 'Edit',
    },
    {
      id:'questions', required:true,
      done: (stats?.questions || 0) >= 1,
      label: 'Questions', icon: '❓',
      desc: stats ? `${stats.questions} question${stats.questions !== 1 ? 's' : ''} added` : '…',
      action: 'questions', actionLabel: 'Add',
    },
    {
      id:'students', required:true,
      done: (stats?.students || 0) >= 1,
      label: 'Students Enrolled', icon: '👥',
      desc: stats
        ? stats.students >= 1
          ? `${stats.students} student${stats.students !== 1 ? 's' : ''} actively enrolled`
          : 'No students enrolled — map groups or create a registration link'
        : '…',
      action: 'links', actionLabel: 'Enroll',
    },
    // ── RECOMMENDED ────────────────────────────────────────────────────────
    {
      id:'proctor', required:false,
      done: (stats?.instructors || 0) >= 1,
      label: 'Proctor Assigned', icon: '👨‍🏫',
      desc: stats ? `${stats.instructors} proctor${stats.instructors !== 1 ? 's' : ''} assigned` : '…',
      action: 'proctor', actionLabel: 'Assign',
    },
    {
      id:'instructions', required:false,
      done: !!(exam.instructions?.trim()),
      label: 'Instructions', icon: '📋',
      desc: exam.instructions ? 'Written' : 'Not added yet',
      action: 'edit', actionLabel: 'Add',
    },
    {
      id:'timing', required:false,
      done: !!(exam.start_time),
      label: 'Date & Time', icon: '📅',
      desc: exam.start_time
        ? new Date(exam.start_time).toLocaleString('en-IN',{timeZone:'Asia/Kolkata',day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:true}) + ' IST'
        : 'Not scheduled',
      action: 'edit', actionLabel: 'Set',
    },
  ];

  const required     = checks.filter(c => c.required);
  const optional     = checks.filter(c => !c.required);
  const reqDone      = required.filter(c => c.done).length;
  const allReqDone   = reqDone === required.length;
  const totalDone    = checks.filter(c => c.done).length;
  const pct          = Math.round((totalDone / checks.length) * 100);

  const statusColor  = allReqDone ? '#15803d' : '#dc2626';
  const statusBg     = allReqDone ? '#dcfce7' : '#fee2e2';
  const barColor     = allReqDone ? '#16a34a' : '#dc2626';

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>

      {/* ── Status banner ────────────────────────────────────────────── */}
      <div style={{
        padding:'12px 14px', borderRadius:10, marginBottom:12,
        background: statusBg,
        border: `1px solid ${allReqDone ? '#86efac' : '#fca5a5'}`,
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <div style={{ fontSize:12, fontWeight:700, color:statusColor }}>
            {allReqDone ? '✅ Ready to Publish' : `⛔ ${required.length - reqDone} Required Task${required.length - reqDone !== 1 ? 's' : ''} Pending`}
          </div>
          <div style={{ fontSize:13, fontWeight:800, color:statusColor }}>{pct}%</div>
        </div>
        {/* Progress bar */}
        <div style={{ height:6, background:'rgba(0,0,0,0.08)', borderRadius:3, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${pct}%`, background:barColor, borderRadius:3, transition:'width 0.4s ease' }} />
        </div>
        {!allReqDone && (
          <div style={{ fontSize:11, color:'#991b1b', marginTop:6 }}>
            Complete required tasks before publishing or opening the exam.
          </div>
        )}
      </div>

      {/* ── Required tasks ───────────────────────────────────────────── */}
      <div style={{ fontSize:10, fontWeight:800, color:'#dc2626', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:6 }}>
        Required to Publish
      </div>
      {loading ? (
        <div style={{ padding:'16px 0', textAlign:'center' }}><div className="spinner" style={{ width:24, height:24, borderWidth:3, margin:'0 auto' }} /></div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:5, marginBottom:12 }}>
          {required.map(item => (
            <CheckRow key={item.id} item={item} onAction={onAction} readOnly={readOnly} />
          ))}
        </div>
      )}

      {/* ── Recommended tasks ────────────────────────────────────────── */}
      <div style={{ fontSize:10, fontWeight:800, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:6 }}>
        Recommended
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
        {optional.map(item => (
          <CheckRow key={item.id} item={item} onAction={onAction} readOnly={readOnly} />
        ))}
      </div>

      {/* ── Refresh button ───────────────────────────────────────────── */}
      <button onClick={load} className="btn btn-secondary btn-sm"
        style={{ marginTop:12, justifyContent:'center', fontSize:11, opacity:0.7 }}>
        ↻ Refresh Status
      </button>
    </div>
  );
}

function CheckRow({ item, onAction, readOnly }) {
  return (
    <div style={{
      display:'flex', gap:8, alignItems:'center',
      padding:'7px 10px', borderRadius:8,
      background: item.done
        ? 'rgba(16,185,129,0.07)'
        : item.required
          ? 'rgba(239,68,68,0.06)'
          : 'var(--bg-elevated)',
      border: `1px solid ${item.done ? 'rgba(16,185,129,0.25)' : item.required ? 'rgba(239,68,68,0.18)' : 'var(--border)'}`,
    }}>
      {/* Icon */}
      <div style={{
        width:22, height:22, borderRadius:11, flexShrink:0,
        display:'flex', alignItems:'center', justifyContent:'center',
        background: item.done ? '#16a34a' : item.required ? 'rgba(239,68,68,0.12)' : 'var(--bg-card)',
        border: `1.5px solid ${item.done ? '#16a34a' : item.required ? '#dc2626' : 'var(--border)'}`,
        fontSize:11,
      }}>
        {item.done
          ? <span style={{ color:'#fff', fontWeight:700, fontSize:12 }}>✓</span>
          : item.required
            ? <span style={{ color:'#dc2626', fontWeight:700, fontSize:11 }}>!</span>
            : <span style={{ color:'var(--text-muted)', fontSize:10 }}>○</span>
        }
      </div>
      {/* Text */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12, fontWeight:600, color: item.done ? '#15803d' : item.required ? '#dc2626' : 'var(--text-primary)', lineHeight:1.2 }}>
          {item.icon} {item.label}
        </div>
        <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {item.desc}
        </div>
      </div>
      {/* Action */}
      {!item.done && onAction && !readOnly && (
        <button onClick={() => onAction(item.action)}
          style={{ fontSize:10, padding:'2px 7px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-card)', cursor:'pointer', color:'var(--text-secondary)', flexShrink:0, whiteSpace:'nowrap' }}>
          {item.actionLabel}
        </button>
      )}
    </div>
  );
}

// Export a hook to check if exam passes checklist gate
export function useChecklistGate(exam, stats) {
  if (!exam || !stats) return { passed: false, missing: ['Loading...'] };
  const missing = [];
  if (!exam.title?.trim())   missing.push('Add exam title and details');
  if ((stats.questions||0) < 1) missing.push('Add at least 1 question');
  if ((stats.students||0)  < 1) missing.push('Enroll at least 1 student');
  return { passed: missing.length === 0, missing };
}
